import { applyTheme } from "@vavr/ui";
import { StrictMode, Suspense, lazy } from "react";
import { createRoot } from "react-dom/client";
import "@vavr/ui/styles.css";
import "@vavr/screens/shared/screens.css";
import { PresentationRoot, Spinner } from "@vavr/ui";
import { configureApi, match, navigate, slot, usePath } from "@vavr/screens/shared";
import { installUi } from "@vavr/modules/ui";
import { WebAccountFrame } from "./WebFrame.tsx";
import { WebConsoleFrame } from "./WebConsoleFrame.tsx";
import "./web.css";

// Module access and screens (docs/spec/modules.md), before anything renders.
installUi();

// Each surface is its own chunk: the kiosk never downloads the console, and vice versa.
const Console = lazy(() => import("@vavr/screens/console").then((m) => ({ default: m.ConsoleApp })));
const Platform = lazy(() => import("@vavr/screens/platform").then((m) => ({ default: m.PlatformApp })));
// Vavr Ops for employees (PMU-286): the whole page on an ops.* host, its own chunk for nobody else.
const Ops = lazy(() => import("@vavr/screens/ops").then((m) => ({ default: m.OpsApp })));
const OPS_HOST = /^ops[.-]/.test(location.hostname); // ops.vavr.us; ops-stage.vavr.us (one level, for the certificate)
// Vavr Ops can be added to a phone's Home Screen (iPhones need that for push alerts).
if (OPS_HOST) {
  for (const [rel, href] of [["manifest", "/ops.webmanifest"], ["apple-touch-icon", "/favicon.svg"]] as const) {
    const l = document.createElement("link"); l.rel = rel; l.href = href; document.head.appendChild(l);
  }
  const m = document.createElement("meta"); m.name = "apple-mobile-web-app-capable"; m.content = "yes"; document.head.appendChild(m);
}
const Kiosk = lazy(() => import("@vavr/screens/kiosk").then((m) => ({ default: m.KioskApp })));
const Guardian = lazy(() => import("@vavr/screens/guardian").then((m) => ({ default: m.GuardianWeb })));
const Verify = lazy(() => import("@vavr/screens/verify").then((m) => ({ default: m.VerifyPage })));
const Login = lazy(() => import("@vavr/screens/auth").then((m) => ({ default: m.Login })));
const Signup = lazy(() => import("@vavr/screens/auth").then((m) => ({ default: m.Signup })));
const Security = lazy(() => import("@vavr/screens/auth").then((m) => ({ default: m.Security })));
const MagicLink = lazy(() => import("@vavr/screens/auth").then((m) => ({ default: m.MagicLink })));
const AcceptInvite = lazy(() => import("@vavr/screens/auth").then((m) => ({ default: m.AcceptInvite })));
const DeleteAccount = lazy(() => import("@vavr/screens/auth").then((m) => ({ default: m.DeleteAccountPage })));
const VerifyEmail = lazy(() => import("@vavr/screens/auth").then((m) => ({ default: m.VerifyEmail })));
const ViewStart = lazy(() => import("@vavr/screens/auth").then((m) => ({ default: m.ViewStart })));

configureApi({
  base: "",
  onUnauthenticated: () => {
    if (OPS_HOST) return; // Vavr Ops shows its own IdenX sign-in
    const p = location.pathname;
    if (p.startsWith("/console") || p.startsWith("/platform") || p.startsWith("/account")) navigate(`/login?next=${encodeURIComponent(p)}`, true);
    else if (p.startsWith("/guardian")) navigate("/guardian/sign-in", true);
  },
});

function App() {
  const path = usePath();
  if (OPS_HOST) return <PresentationRoot className="vavr-web" presentation="console"><Suspense fallback={<div className="pmu-center"><Spinner label="Loading" /></div>}><Ops /></Suspense></PresentationRoot>;
  const next = new URLSearchParams(location.search).get("next");
  // A module's public page (a school's calendar): no sign-in, no frame of ours around it.
  const open = slot("public.page").map((p) => ({ p, params: match(`/${p.path}`, path.replace(/\/$/, "")) })).find((x) => x.params);
  if (open) return <PresentationRoot className={`vavr-web vavr-public${open.p.embed ? " vavr-embed" : ""}`} presentation="console">{open.p.render(open.params!)}</PresentationRoot>;
  const page = (() => {
    if (path.startsWith("/console")) return <Console />;
    if (path.startsWith("/platform")) return <Platform />;
    if (path.startsWith("/kiosk")) return <Kiosk />;
    if (path.startsWith("/guardian")) return <Guardian />;
    if (path.startsWith("/verify-email")) return <VerifyEmail />;
    if (path === "/view") return <ViewStart />;
    if (path.startsWith("/verify")) return <Verify />;
    if (path === "/signup") return <Signup />;
    if (path === "/account/security") return <Security />;
    if (path === "/auth/magic") return <MagicLink />;
    if (path === "/invite") return <AcceptInvite />;
    if (path === "/delete-account") return <DeleteAccount />;
    if (path === "/login" || path === "/") return <Login next={next && next.startsWith("/") ? next : "/console"} />;
    return <Login />;
  })();
  const appSurface = path.startsWith("/kiosk") || path.startsWith("/guardian");
  const consolePage = path.startsWith("/console") || path.startsWith("/platform");
  const accountPage = !appSurface && !path.startsWith("/console") && !path.startsWith("/platform") && !path.startsWith("/verify");
  const content = <Suspense fallback={<div className="pmu-center"><Spinner label="Loading" /></div>}>{page}</Suspense>;
  return (
    <PresentationRoot className={appSurface ? undefined : "vavr-web"} presentation={path.startsWith("/kiosk") ? "kiosk" : path.startsWith("/guardian") ? "mobile" : "console"}>
      {accountPage ? <WebAccountFrame signup={path === "/signup"}>{content}</WebAccountFrame> : consolePage ? <WebConsoleFrame>{content}</WebConsoleFrame> : content}
    </PresentationRoot>
  );
}

// The device's appearance choice (light, dark, or follow the device) before the first paint.
applyTheme();
createRoot(document.getElementById("root")!).render(<StrictMode><App /></StrictMode>);
